import React from "react";
import ReactDOM from "react-dom";
import Log, { isLogIgnored } from "../lib/Log";
import _ from "underscore";
import Language from "../lib/Language";
import { TReferenceItem } from "../types/api/shared";
import { LDFlagSet } from "launchdarkly-react-client-sdk";

const FADE_TIMEOUT = 5000;
const EXIT_ANIM_TIMEOUT = 4000;

type TMessageBarProps = {
    flags: LDFlagSet;
    style: React.CSSProperties;
};

type TMessageBarState = {
    message?: string | null;
    color?: string | null;
    isImportant: boolean;
    url?: string;
    details: string[];
};

class MessageBar extends React.Component<TMessageBarProps, TMessageBarState> {
    static displayName = "MessageBar";

    state = {
        message: null,
        color: null,
        isImportant: false,
        url: "",
        details: [],
    };

    _isMounted = false;
    _unlistenLog: (() => void) | null = null;
    _timeoutId: any = 0;
    _exitAnimationTimeoutId: any = 0;

    componentDidMount() {
        this._isMounted = true;
        const listener = (logEntry) => {
            if (isLogIgnored(logEntry.context)) {
                return;
            }
            let color = "red";
            let isImportant = false;
            if (logEntry.level < Log.WARNING) {
                isImportant = true;
            }
            if (logEntry.level === Log.WARNING) {
                color = "yellow";
            }
            if (logEntry.level > Log.WARNING) {
                color = "blue";
            }
            const url = logEntry.context ? logEntry.context.url : undefined;
            const details = logEntry.context ? logEntry.context.details : [];
            if (this.props.flags?.enableMessageBarCopyButton && details && details.length > 0) {
                isImportant = true;
            }
            this.setState({
                message: logEntry.message,
                color,
                isImportant: isImportant || logEntry.context.isImportant,
                url,
                details,
            });
        };
        // The non-recoverable levels are deliberately left out - those are presented in a
        // blocking modal by NonRecoverableErrorModal instead.
        this._unlistenLog = Log.listen(listener, Log.RECOVERABLE_LEVELS);
    }

    componentWillUnmount() {
        this._isMounted = false;
        this._unlistenLog?.();
        this._unlistenLog = null;
        clearTimeout(this._timeoutId);
        clearTimeout(this._exitAnimationTimeoutId);
    }

    componentDidUpdate() {
        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
            clearTimeout(this._exitAnimationTimeoutId);
        }
        if (!this.state.isImportant) {
            this._exitAnimationTimeoutId = setTimeout(() => {
                if (this._isMounted) {
                    (ReactDOM.findDOMNode(this) as HTMLElement).classList.add("messageBarFadeOut");
                }
            }, EXIT_ANIM_TIMEOUT);
            this._timeoutId = setTimeout(this.dismiss, FADE_TIMEOUT);
        }
    }

    dismissActive = () => {
        // Invoked when the user clicks the dismissal-X of the message bar.
        this.dismiss();
    };

    dismiss = () => {
        if (this._isMounted) {
            this.setState({
                message: null,
                color: null,
            });
        }
    };

    stopFade = () => {
        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
            clearTimeout(this._exitAnimationTimeoutId);
        }
    };

    resumeFade = () => {
        if (!this.state.isImportant) {
            const self = this;
            this._exitAnimationTimeoutId = setTimeout(() => {
                if (self._isMounted) {
                    (ReactDOM.findDOMNode(self) as HTMLElement).classList.add("messageBarFadeOut");
                }
            }, EXIT_ANIM_TIMEOUT);
            this._timeoutId = setTimeout(this.dismiss, FADE_TIMEOUT);
        }
    };

    copyToClipboard = () => {
        if (!this.state.details || this.state.details.length === 0) {
            navigator.clipboard.writeText(`${this.state.message}`);
        } else {
            navigator.clipboard.writeText(
                `${this.state.message}\n${
                    this.state.details
                        ? this.state.details
                              .map(
                                  (
                                      detail: TReferenceItem // A reference item object has entityType: string, id: number, and label: string
                                  ) => detail.label
                              )
                              .join("\n")
                        : null
                }`
            );
        }
    };

    renderDetails = () => {
        if (!this.props.flags?.enableMessageBarCopyButton) {
            return null;
        }
        return (
            <button
                className="deprecated dialogCopyButton"
                style={{
                    fontFamily: "FontAwesome",
                    width: "25px",
                    color: "white",
                    backgroundColor: "unset",
                    border: "none",
                    float: "none",
                }}
                onClick={this.copyToClipboard}
                title={Language.get("nc_copy_reservations_to_clipboard")}
            >
                &#xf0ea;
            </button>
        );
    };

    render() {
        const style = _.clone(this.props.style);
        const message = this.state.message;
        if (!message || message === Log.ALL_LOADS_FINISHED_MSG) {
            return <span />;
        }

        const classNames = ["messageBar"];
        const color = this.state.color;
        if (color) {
            classNames.push(color);
        }
        if (this.state.isImportant === true) {
            classNames.push("important");
        }
        if (color === "blue") {
            style.width = undefined;
        }

        classNames.push("messageBarFadeIn");

        const actionButtons = (
            <span style={{ position: "absolute", right: "5px" }}>
                {this.renderDetails()}
                <button
                    className="deprecated closeButton"
                    onClick={this.dismissActive}
                    style={{ width: "25px", float: "none", position: "unset" }}
                ></button>
            </span>
        );

        if (this.state.url) {
            return (
                <div
                    className={classNames.join(" ")}
                    style={style}
                    onMouseOver={this.stopFade}
                    onMouseOut={this.resumeFade}
                >
                    <div className="messageContent">
                        <a href={this.state.url}>{message}</a>
                        {actionButtons}
                    </div>
                </div>
            );
        }

        return (
            <div
                className={classNames.join(" ")}
                style={style}
                onClick={this.dismissActive}
                onMouseOver={this.stopFade}
                onMouseOut={this.resumeFade}
            >
                <div className="messageContent">
                    {message}
                    {actionButtons}
                </div>
            </div>
        );
    }

    shouldComponentUpdate(nextProps, nextState) {
        const willUpdateState =
            nextState.message !== this.state.message ||
            nextState.color !== this.state.color ||
            nextState.isImportant !== this.state.isImportant ||
            nextState.url !== this.state.url;

        if (willUpdateState) {
            return true;
        }
        return false;
    }
}

export default MessageBar;
